// The card request's background film (founder, 2026-11-03, "final-right" v13 from the animator). One 33.5-second film
// on #F3F4FC with one stop per screen (five). Held stops rest on a frame; the tunnel and tile stops loop an 8-second
// clip while the person is on that screen. Continue plays forward to the next stop, Back plays backward to the one
// before, faster (to 1.5x) the further the film trails. Holding is a paused <video>; playing is
// the browser's own decoder, so nothing is drawn in script.
//
// Files: film-fwd (the whole film), film-rev (the same film reversed, for Back) and one clip per loop. A loop clip's
// frame k is the film's frame entry+k, pixel for pixel, and the film's exit frame equals its entry frame, so the
// player can move between the film and a loop on any frame with no visible cut.
import { useEffect, useLayoutEffect } from 'react'
import fwdSrc from './film/film-fwd.mp4'
import revSrc from './film/film-rev.mp4'
import tunnelSrc from './film/loop-tunnel.mp4'
import tilesSrc from './film/loop-tiles.mp4'
import poster from './film/poster.jpg'

const FPS = 30
const FRAMES = 1005
const DURATION = FRAMES / FPS
const LOOP_FRAMES = 240

type Stop = { kind: 'hold'; frame: number } | { kind: 'loop'; loop: LoopId }
type LoopId = 'tunnel' | 'tiles'
const LOOPS: Record<LoopId, { entry: number; src: string }> = {
  tunnel: { entry: 243, src: tunnelSrc }, tiles: { entry: 579, src: tilesSrc },
}
/** ernd-final-right-timing.json (v13, same timing as v11): one stop per screen. Arrival, the fan, the tunnel (loops), the tile rows
 *  (loop), the front/back reveal. */
const STOPS: Stop[] = [
  { kind: 'hold', frame: 60 }, { kind: 'hold', frame: 180 }, { kind: 'loop', loop: 'tunnel' }, { kind: 'loop', loop: 'tiles' },
  { kind: 'hold', frame: 981 },
]
/** Where the film rests for a stop: a hold's frame, or a loop's entry (its frame 0). */
const restFrame = (s: Stop) => (s.kind === 'hold' ? s.frame : LOOPS[s.loop].entry)
const RESTS = STOPS.map(restFrame)
/** One film for the whole request. Screen 2's Continue creates the company and swaps the start page for the apply
 *  page, with a moment of loading between. A film inside either page would be torn down and rebuilt there, a blank
 *  cut on the way from the fan to the tunnel; this one lives on <body>, keeps playing through the swap, and goes
 *  only when the person leaves the request. */
type Shared = {
  box: HTMLDivElement; F: HTMLVideoElement; R: HTMLVideoElement; T: HTMLVideoElement; G: HTMLVideoElement
  st: { f: number; mode: 'fwd' | 'rev' | 'loop'; loop: LoopId | null; target: number; targetLoop: LoopId | null
    raf: number; started: boolean; leaving: boolean; dead: boolean }
  users: number
}
let shared: Shared | null = null
const REQUEST_PATH = '/card/apply'

function video(src: string, preload: 'auto' | 'none', posterSrc?: string): HTMLVideoElement {
  const v = document.createElement('video')
  v.className = 'film-v'
  v.muted = true; v.defaultMuted = true; v.playsInline = true
  v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('disablepictureinpicture', '')
  v.preload = preload
  if (posterSrc) v.poster = posterSrc
  v.src = src
  return v
}

function film(): Shared {
  if (shared) return shared
  const box = document.createElement('div')
  box.className = 'film'
  box.setAttribute('aria-hidden', 'true')
  const F = video(fwdSrc, 'auto', poster), R = video(revSrc, 'none'), T = video(tunnelSrc, 'none'), G = video(tilesSrc, 'none')
  F.classList.add('is-on')
  box.append(F, R, T, G)
  document.body.prepend(box)
  // the backward copy, then both loop clips (about 1 MB each), once the forward film can play: no cut waits on the network
  const later = () => { for (const v of [R, T, G]) if (v.preload !== 'auto') { v.preload = 'auto'; v.load() } }
  if (F.readyState >= 3) later(); else F.addEventListener('canplaythrough', later, { once: true })
  shared = { box, F, R, T, G, users: 0,
    st: { f: 0, mode: 'fwd', loop: null, target: 0, targetLoop: null, raf: 0, started: false, leaving: false, dead: false } }
  return shared
}

/** Removes the film once the person has left the request (not when one page of it swaps for the next). */
function release() {
  const sh = shared
  if (!sh) return
  sh.users--
  setTimeout(() => {
    if (shared !== sh || sh.users > 0 || window.location.pathname === REQUEST_PATH) return
    cancelAnimationFrame(sh.st.raf)
    sh.st.dead = true                                // late 'seeked'/'loadedmetadata' callbacks do nothing now
    for (const v of [sh.F, sh.R, sh.T, sh.G]) { v.pause(); v.removeAttribute('src'); v.load() }
    sh.box.remove()
    shared = null
  }, 0)
}

const MAX_RATE = 1.5
const FULL_SPEED_AT = 20

function rateFor(frame: number, target: number): number {
  const lo = Math.min(frame, target), hi = Math.max(frame, target)
  const between = RESTS.filter((r, i) => r > lo + 1 && r <= hi + 1 && RESTS[i - 1] !== r).length
  return Math.min(MAX_RATE, 1 + (MAX_RATE - 1) * Math.max(0, between - 1) / (FULL_SPEED_AT - 1))
}

/** `pose` is the screen's stop (0-4), or -1 on the welcome screen (the film's first frame). Renders nothing itself:
 *  the film is the shared one on <body>. */
export function CardFilm({ pose }: { pose: number }) {
  const still = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  useLayoutEffect(() => {
    const sh = film()
    sh.users++
    return release
  }, [])

  useEffect(() => {
    const { F, R, T, G, st: s } = film()
    // One <video> per loop: swapping one element's src blanks the screen while the new clip loads (the cut from the
    // tunnel to the tiles flashed).
    const loopEl = (id: LoopId) => (id === 'tunnel' ? T : G)
    const ALL = [F, R, T, G]
    const ready = (v: HTMLVideoElement) => { if (v.preload !== 'auto') { v.preload = 'auto'; v.load() } }
    const stop = pose < 0 ? null : STOPS[Math.min(pose, STOPS.length - 1)]
    s.target = stop ? restFrame(stop) : 0
    s.targetLoop = stop?.kind === 'loop' ? stop.loop : null

    // The first real screen decides the start: screen 1 plays in from the film's start; any later screen (a resume
    // or a reload) lands on its stop at once. The welcome screen and loading show frame 0. Moving from the start page
    // to the apply page keeps the same film, already started, so it plays on.
    if (!s.started) {
      if (pose < 0) { seekFilm(F, 0); s.f = 0; return }
      s.started = true
      if (pose > 0 || still) { land(); return }
    }
    if (still) { land(); return }
    drive()

    /** Straight to the stop with no travel (a reload mid-request, or reduced motion). */
    function land() {
      cancelAnimationFrame(s.raf)
      s.f = s.target          // at once, so a second run of this effect (React's dev double-run) sees us arrived
      if (s.targetLoop && !still) { startLoop(s.targetLoop, 0); return }
      showOnly(F); seekFilm(F, s.target); s.f = s.target; s.mode = 'fwd'
    }

    function drive() {
      cancelAnimationFrame(s.raf)
      // Already looping the loop we want: nothing to do (a re-render on the same screen doesn't restart it).
      if (s.mode === 'loop' && s.loop === s.targetLoop) { const L = loopEl(s.loop!); s.leaving = false; L.loop = true; if (L.paused) void L.play().catch(() => undefined); return }
      if (s.mode === 'loop') {
        const L = loopEl(s.loop!)
        // Leaving a loop: forward waits for the end of the cycle (the exit frame), Back turns at the frame on screen.
        const k = Math.floor(L.currentTime * FPS) % LOOP_FRAMES
        const entry = LOOPS[s.loop!].entry
        if (s.target > entry) {
          s.leaving = true
          const wait = () => {
            if (!s.leaving) return
            if (L.currentTime * FPS >= LOOP_FRAMES - 1.5 || L.ended) {
              L.pause(); s.leaving = false
              // the film's exit frame looks the same as the loop's frame 0; carry on from there in the film
              s.f = entry + LOOP_FRAMES; travel(1)
              return
            }
            s.raf = requestAnimationFrame(wait)
          }
          L.loop = false
          s.raf = requestAnimationFrame(wait)
          return
        }
        L.pause()
        s.f = entry + k
        travel(-1)
        return
      }
      if (Math.abs(s.target - s.f) < 0.5) { if (s.targetLoop) startLoop(s.targetLoop, 0); return }
      travel(s.target > s.f ? 1 : -1)
    }

    /** Play the film one way toward s.target; on arrival, hold or start the stop's loop. */
    function travel(dir: 1 | -1) {
      if (s.targetLoop) ready(loopEl(s.targetLoop))
      const v = dir === 1 ? F : R
      const other = dir === 1 ? R : F
      other.pause()
      seekFilm(v, s.f, dir, () => {
        if (s.dead) return
        showOnly(v)
        s.mode = dir === 1 ? 'fwd' : 'rev'
        v.playbackRate = rateFor(s.f, s.target)
        void v.play().catch(() => undefined)
        let goal = s.target
        const tick = () => {
          const f = dir === 1 ? v.currentTime * FPS : FRAMES - v.currentTime * FPS
          s.f = f
          // A Back (or an answer) while we play may move the stop behind us: turn round. Only a moved stop does;
          // running a little past the stop between two frames is just arriving.
          if (s.target !== goal && ((dir === 1 && s.target < f - 0.5) || (dir === -1 && s.target > f + 0.5))) { v.pause(); travel(dir === 1 ? -1 : 1); return }
          goal = s.target
          if (dir === 1 ? f >= s.target - 0.5 : f <= s.target + 0.5) {
            v.pause()
            s.f = s.target
            if (s.targetLoop && s.target === LOOPS[s.targetLoop].entry) startLoop(s.targetLoop, 0)
            else seekFilm(v, s.target, dir)
            return
          }
          const r = rateFor(f, s.target)
          if (Math.abs(v.playbackRate - r) > 0.02) v.playbackRate = r
          s.raf = requestAnimationFrame(tick)
        }
        s.raf = requestAnimationFrame(tick)
      })
    }

    function startLoop(id: LoopId, k: number) {
      cancelAnimationFrame(s.raf)
      const L = loopEl(id)
      ready(L)
      L.loop = true
      const go = () => {
        L.currentTime = k / FPS
        L.addEventListener('seeked', () => { if (s.dead) return; showOnly(L); s.mode = 'loop'; s.loop = id; s.f = LOOPS[id].entry; L.playbackRate = 1; void L.play().catch(() => undefined) }, { once: true })
      }
      if (L.readyState >= 1) go(); else L.addEventListener('loadedmetadata', go, { once: true })
    }

    function showOnly(v: HTMLVideoElement) {
      for (const x of ALL) x.classList.toggle('is-on', x === v)
      for (const x of ALL) if (x !== v) x.pause()
    }
  }, [pose, still])

  return null
}

/** Shows film frame `f` on `v` (the forward copy, or the reversed one when dir is -1). */
function seekFilm(v: HTMLVideoElement, f: number, dir: 1 | -1 = 1, then?: () => void) {
  // aim at the middle of the frame, not its edge; reversed, film frame f is clip frame FRAMES-1-f
  const t = dir === 1 ? (f + 0.5) / FPS : (FRAMES - 0.5 - f) / FPS
  const go = () => {
    const at = Math.max(0, Math.min(DURATION - 0.5 / FPS, t))
    if (then) {
      if (Math.abs(v.currentTime - at) < 0.5 / FPS) { then(); return }
      v.addEventListener('seeked', then, { once: true })
    }
    v.currentTime = at
  }
  if (v.readyState >= 1) go(); else v.addEventListener('loadedmetadata', go, { once: true })
}
